import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Button, FormControlLabel, Stack, Switch } from '@mui/material';
import Typography from '@mui/material/Typography';
import { OPERATOR_COMMUNICATION_SETTINGS } from '../../../services/utils/units-constants';

/**
 * Communication settings of an operator, used as step in the operator creation
 * (createEdit="create") and in the "Kommunikationseinstellungen bearbeiten" workflow (createEdit="edit").
 */
function OperatorCommunicationSettings({
  payload,
  setPayload,
  submit,
  setCanNext,
  setSubmit,
  createEdit,
}) {
  const { t } = useTranslation();

  const validationSchema = yup.object().shape({
    sendEmailToContractUsers: yup.boolean(),
    automaticSendBillEmail: yup.boolean(),
    technicianSendConnectorFaultReport: yup.boolean(),
  });

  useEffect(() => {
    if (submit > 0) document.getElementById('subCommunicationSettings').click();
    setSubmit(0);
  }, [submit]);

  const formik = useFormik({
    // pre-filled from the operator on edit, defaults if a value is missing
    initialValues: OPERATOR_COMMUNICATION_SETTINGS.reduce(
      (values, setting) => ({
        ...values,
        [setting.name]: payload?.[setting.name] ?? setting.defaultValue,
      }),
      {}
    ),
    validationSchema: validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: (values) => {
      const payloadToSet = {
        sendEmailToContractUsers: values.sendEmailToContractUsers,
        automaticSendBillEmail: values.automaticSendBillEmail,
        technicianSendConnectorFaultReport:
          values.technicianSendConnectorFaultReport,
      };
      if (createEdit === 'edit') {
        payloadToSet.cpoId = payload?.cpoId;
      }
      setPayload(payloadToSet);
    },
  });

  // all combinations are valid, so the step can always be confirmed
  useEffect(() => {
    setCanNext(Boolean(formik?.isValid));
  }, [formik?.isValid]);

  return (
    <>
      <form onSubmit={formik?.handleSubmit} noValidate>
        <Typography
          fontWeight={700}
          variant="subtitle1"
          id="communication-settings-label"
        >
          {t('LBLCommunicationSettings')}
        </Typography>
        {OPERATOR_COMMUNICATION_SETTINGS.map((setting) => (
          <Stack
            key={setting.name}
            my={3}
            direction="row"
            alignItems="center"
            spacing={2}
          >
            <FormControlLabel
              control={
                <Switch
                  id={setting.name}
                  name={setting.name}
                  checked={formik.values[setting.name] || false}
                  onChange={(e) => {
                    formik.setFieldValue(setting.name, e.target.checked);
                  }}
                  color="primary"
                />
              }
              label={t(setting.label)}
            />
          </Stack>
        ))}
        {/**
         * this button is hidden and used to trigger submit from outside of the component
         */}
        <Button
          sx={{ display: 'none' }}
          id="subCommunicationSettings"
          type="submit"
        ></Button>
      </form>
    </>
  );
}

export default OperatorCommunicationSettings;
